import { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import PropTypes from 'prop-types';

import { patchSnSalesWarranty } from '#/api/crmMgmt/snSalesWarranty';
import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import FloatingDateInput from '#/modules/commonComponents/form/FloatingDateInput';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import FloatingSelect from '#/modules/commonComponents/form/select';
import {
  WARRANTY_POLICY_OPTIONS,
  buildWarrantyPatchPayload,
  getSnSalesWarrantyErrorMessage,
  isSnSalesWarrantyVersionConflict,
  mapWarrantyToCorrectionForm,
  warrantyCorrectionFormSchema,
} from '#/modules/crmMgmt/salesOrder/helper/snSalesWarranty';
import { toast } from '#/utils/toast';

/** 只更正銷售日／政策／到期日，必填原因；客戶、來源、登錄世代不可改。 */
const SnWarrantyCorrectionForm = ({ mtrSnId, warranty, onCorrected }) => {
  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(warrantyCorrectionFormSchema),
    defaultValues: mapWarrantyToCorrectionForm(warranty),
  });
  const warrantyPolicy = useWatch({ control, name: 'warrantyPolicy' });

  // 重新讀取（成功或版本衝突）後以最新值帶入
  useEffect(() => {
    reset(mapWarrantyToCorrectionForm(warranty));
  }, [warranty, reset]);

  const patchMutation = useMutation({
    mutationFn: (values) => patchSnSalesWarranty(mtrSnId, buildWarrantyPatchPayload({
      ...values,
      updatedAt: warranty?.updatedAt,
    })),
  });

  async function onSubmit(values) {
    try {
      const result = await patchMutation.mutateAsync(values);
      toast.success(result?.message || '保固已更正');
      onCorrected?.();
    } catch (error) {
      toast.error(getSnSalesWarrantyErrorMessage(error, '保固更正失敗'));
      if (isSnSalesWarrantyVersionConflict(error)) {
        onCorrected?.();
      }
    }
  }

  return (
    <form className="mt-4" onSubmit={handleSubmit(onSubmit)} noValidate>
      <h3 className="mb-2 text-sm font-medium text-gray-900">更正保固</h3>
      <div className={cn('grid grid-cols-1 gap-3', '@xl/sheet:grid-cols-2')}>
        <FloatingDateInput
          id="salesDate"
          label="銷售日"
          control={control}
          errors={errors}
        />
        <FloatingSelect
          id="warrantyPolicy"
          label="保固政策"
          options={WARRANTY_POLICY_OPTIONS}
          control={control}
          errors={errors}
        />
        {warrantyPolicy === 'CUSTOM' ? (
          <FloatingDateInput
            id="warrantyExpiryDate"
            label="保固迄日"
            control={control}
            errors={errors}
          />
        ) : null}
        <FloatingInput
          id="reason"
          type="text"
          label="更正原因"
          register={register}
          errors={errors}
          containerClassName="@xl/sheet:col-span-2"
        />
      </div>
      <div className="mt-2 flex justify-end">
        <Button type="submit" disabled={patchMutation.isPending}>
          {patchMutation.isPending ? '更正中…' : '更正保固'}
        </Button>
      </div>
    </form>
  );
};

SnWarrantyCorrectionForm.propTypes = {
  mtrSnId: PropTypes.string.isRequired,
  warranty: PropTypes.object.isRequired,
  onCorrected: PropTypes.func,
};

export default SnWarrantyCorrectionForm;
